/**
 * Purchase components:
 *   <db-buybox-component>  tiers, colours, mix-and-match slots, add to cart
 *   <db-sticky-component>  the phone bar that follows the shopper down the page
 *   <db-cta-component>     the add-to-cart button in the closing call to action
 *
 * Cart updates go through the same standard events the theme's own product form uses, so the
 * cart drawer, cart count and analytics react exactly as they do for any other add-to-cart.
 * Each successful add and each tier or colour choice also reports a db_* event (see assets/db-events.js).
 */
import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { store } from '@theme/db-core';
import { emit } from '@theme/db-events';
import { getScrollTop } from '@theme/scroll-container';

const ADDED_FLASH_MS = 1500;

/** @param {string} template @param {Record<string, string | number>} values */
function fill(template, values) {
  let out = template;
  for (const [key, value] of Object.entries(values)) out = out.replaceAll(key, String(value));
  return out;
}

/** "Pink, Ice Blue and Red Orange" @param {string[]} items @param {string} and */
function listWords(items, and) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

/**
 * What the shopper added, in the shop's current currency (the event's `currency` property), for the `value` property
 * of db_add_to_cart: the bundle price when a whole bundle was added, otherwise the list price of the masks. It leaves
 * out discount codes, shipping and tax.
 * @param {number} count
 * @param {number} [tier]
 * @returns {number}
 */
function addedValue(count, tier) {
  const bundle = tier ? store.data?.tiers.find((entry) => entry.qty === tier && entry.qty === count) : undefined;
  const cents = bundle ? bundle.cents : (store.data?.unit ?? 0) * count;
  return Math.round(cents) / 100;
}

/**
 * Adds the current selection (one cart line per colour) to the cart.
 * @param {HTMLElement} source - element that dispatches the cart events
 * @param {string} [location] - where the shopper pressed the button, for the db_add_to_cart event
 * @returns {Promise<{ok: boolean, message?: string, network?: boolean, count?: number}>}
 */
export async function addSelectionToCart(source, location = '') {
  return addLinesToCart(source, store.lines, { location, tier: store.state.tier });
}

/**
 * Adds explicit cart lines through the theme's standard cart events.
 * @param {HTMLElement} source - element that dispatches the cart events
 * @param {{id: number, quantity: number}[]} lines
 * @param {{location?: string, tier?: number}} [meta] - reported with the db_add_to_cart event
 * @returns {Promise<{ok: boolean, message?: string, network?: boolean, count?: number}>}
 */
export async function addLinesToCart(source, lines, meta = {}) {
  if (!lines.length) return { ok: false };

  const sectionIds = [];
  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  }

  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  const refreshCart = async () => {
    const response = await fetch(`${Theme.routes.cart_url}.json`, { credentials: 'same-origin' });
    return response.json();
  };

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines.map((line) => ({ id: line.id, quantity: line.quantity })),
        sections: sectionIds.join(','),
      }),
    });
    const body = await response.json();

    if (body.status) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: body.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: body.description, errors: body.errors },
        })
      );
      refreshCart()
        .then((cart) =>
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'db-buybox', itemCount, productId: store.data?.productId },
          })
        )
        .catch(deferred.reject);
      return { ok: false, message: body.description || body.message };
    }

    const cart = await refreshCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'db-buybox',
        itemCount,
        productId: store.data?.productId,
        sections: body.sections,
        didError: false,
      },
    });
    emit('db_add_to_cart', {
      qty: itemCount,
      value: addedValue(itemCount, meta.tier),
      tier: meta.tier ?? 0,
      location: meta.location ?? '',
      colour_keys: lines.map((line) => store.variant(line.id)?.key ?? '').join(','),
    });
    return { ok: true, count: itemCount };
  } catch (error) {
    console.error(error);
    deferred.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
    );
    return { ok: false, network: true, message: error?.message };
  }
}

/**
 * @typedef {Object} BuyboxRefs
 * @property {HTMLInputElement[]} tiers
 * @property {HTMLInputElement[]} swatches
 * @property {HTMLElement} slotsWrap
 * @property {HTMLElement} slotPrompt
 * @property {HTMLElement} slotList
 * @property {HTMLElement} summary
 * @property {HTMLElement} notice
 * @property {HTMLButtonElement} atc
 * @property {HTMLElement} atcLabel
 * @property {HTMLElement} atcPrice
 * @property {HTMLElement} error
 * @property {HTMLElement} live
 *
 * @extends {Component<BuyboxRefs>}
 */
class DbBuyboxComponent extends Component {
  requiredRefs = ['atc', 'atcLabel', 'atcPrice'];

  #abort = new AbortController();
  #busy = false;
  /** @type {number | undefined} */
  #flash;

  connectedCallback() {
    super.connectedCallback();
    if (!store.ready) return;

    const { signal } = this.#abort;
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit, { signal });
    if (this.refs.slotsWrap) this.refs.slotsWrap.hidden = false;
    store.subscribe(() => this.#render(), signal);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    clearTimeout(this.#flash);
  }

  /** @param {Event} event */
  handleTier(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    store.setTier(Number(input.value));
    emit('db_tier_pick', { tier: Number(input.value), source: this.dataset.instance ?? '' });
  }

  /** @param {Event} event */
  handleSwatch(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const id = Number(input.value);
    store.pickColour(id);
    emit('db_colour_pick', { colour_key: store.variant(id)?.key ?? '', source: 'swatch' });
  }

  /** @param {Event} event */
  handleSlot(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-index]');
    if (button instanceof HTMLElement) store.setActive(Number(button.dataset.index));
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#busy || this.refs.atc.hasAttribute('disabled')) return;
    this.#busy = true;
    const { atc, atcLabel, error, live } = this.refs;
    error.hidden = true;
    atc.classList.add('is-loading');
    atcLabel.textContent = this.dataset.tAdding ?? '';
    const result = await addSelectionToCart(this, this.dataset.instance ?? 'buybox');
    atc.classList.remove('is-loading');
    this.#busy = false;
    if (result.ok) {
      atcLabel.textContent = this.dataset.tAdded ?? '';
      const count = result.count ?? 0;
      live.textContent =
        count === 1 ? (this.dataset.tAnnounceOne ?? '') : fill(this.dataset.tAnnounceMany ?? '', { __COUNT__: count });
      this.#flash = window.setTimeout(() => {
        this.#flash = undefined;
        live.textContent = '';
        this.#render();
      }, ADDED_FLASH_MS);
    } else {
      error.textContent = result.network ? (this.dataset.tErrorNetwork ?? '') : result.message || this.dataset.tError || '';
      error.hidden = false;
      this.#render();
    }
  };

  #render() {
    const { state } = store;
    const refs = this.refs;
    const tierData = store.tierData;
    const accent = store.accentVariant;

    for (const input of refs.tiers ?? []) input.checked = Number(input.value) === state.tier;

    // colour label: the name is part of the legend, not colour alone
    const label = this.querySelector('[data-db-colour-label]');
    if (label && accent) label.textContent = fill(this.dataset.tColourLabel ?? '', { __NAME__: accent.label });

    // swatches: checked = the colour lighting the page; badge = masks using it
    const counts = new Map();
    for (const id of state.slots) counts.set(id, (counts.get(id) ?? 0) + 1);
    for (const input of refs.swatches ?? []) {
      const id = Number(input.value);
      input.checked = id === state.accent;
      const badge = input.closest('.db-swatch')?.querySelector('.db-swatch__count');
      if (badge instanceof HTMLElement) {
        const count = counts.get(id) ?? 0;
        badge.hidden = state.tier === 1 || count === 0;
        badge.textContent = String(count);
        badge.setAttribute('aria-label', fill(this.dataset.tColourCount ?? '', { __COUNT__: count }));
      }
    }

    // a sold-out colour previewed through a deep link or a tap
    if (refs.notice) {
      const soldOut = Boolean(accent && !accent.available);
      refs.notice.hidden = !soldOut;
      refs.notice.textContent = soldOut && accent ? fill(this.dataset.tSoldOutPick ?? '', { __NAME__: accent.label }) : '';
    }

    // slots
    const list = refs.slotList;
    if (list) {
      // the list is rebuilt on every change; keep keyboard focus on the same mask afterwards
      const focused = document.activeElement instanceof HTMLElement ? document.activeElement.closest('.db-slot') : null;
      const focusedIndex = focused && list.contains(focused) ? Number(focused.dataset.index) : -1;
      list.replaceChildren(
        ...state.slots.map((id, index) => {
          const variant = store.variant(id);
          const li = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'db-slot';
          button.dataset.index = String(index);
          button.setAttribute('on:click', '/handleSlot');
          button.setAttribute('aria-pressed', String(index === state.active));
          button.setAttribute(
            'aria-label',
            fill(this.dataset.tSlotAria ?? '', { __N__: index + 1, __COLOUR__: variant?.label ?? '' })
          );
          button.style.setProperty('--sw', variant?.hex ?? '#fff');
          button.style.setProperty('--sw-rgb', variant?.rgb ?? '255 255 255');
          const dot = document.createElement('span');
          dot.className = 'db-slot__dot';
          dot.setAttribute('aria-hidden', 'true');
          const text = document.createElement('span');
          text.append(fill(this.dataset.tSlotLabel ?? '', { __N__: index + 1 }));
          const name = document.createElement('small');
          name.textContent = variant?.label ?? '';
          text.append(name);
          button.append(dot, text);
          li.append(button);
          return li;
        })
      );
      list.hidden = state.tier === 1;
      if (focusedIndex >= 0) list.querySelector(`.db-slot[data-index="${focusedIndex}"]`)?.focus({ preventScroll: true });
    }
    if (refs.slotPrompt) {
      refs.slotPrompt.textContent =
        state.tier === 1
          ? (this.dataset.tSlotPromptSingle ?? '')
          : fill(this.dataset.tSlotPrompt ?? '', { __N__: state.active + 1 });
    }
    if (refs.summary) {
      const names = state.slots.map((id) => store.variant(id)?.label ?? '');
      refs.summary.textContent =
        state.tier === 1 ? '' : fill(this.dataset.tSummary ?? '', { __LIST__: listWords(names, this.dataset.tAnd ?? 'and') });
    }

    // button
    const soldOutAll = store.available.length === 0;
    refs.atc.toggleAttribute('disabled', soldOutAll);
    if (!this.#busy && !this.#flash) {
      // a bundle says how many masks it adds, so the button never adds more than it names
      const addLabel = state.tier > 1 && this.dataset.tAddCount ? fill(this.dataset.tAddCount, { __N__: state.tier }) : (this.dataset.tAdd ?? '');
      refs.atcLabel.textContent = soldOutAll ? (this.dataset.tSoldOut ?? '') : addLabel;
    }
    if (tierData) refs.atcPrice.textContent = tierData.total;
  }
}

if (!customElements.get('db-buybox-component')) {
  customElements.define('db-buybox-component', DbBuyboxComponent);
}

/**
 * The persistent bar ("Add to cart" on the product page, "Get the mask" elsewhere). It appears once the shopper has
 * scrolled a little and hides while any [data-db-watch="enter"] element (a hero button, a purchase box, the final
 * call to action) is on screen. Dialogs, the cart drawer and the game hide it in CSS. Hidden state uses `inert`, so
 * nothing in it is focusable. While it shows, its height is published as --db-bar-h on <html>.
 *
 * @typedef {Object} StickyRefs
 * @property {HTMLButtonElement | HTMLAnchorElement} atc
 * @property {HTMLElement} atcPrice
 * @property {HTMLElement} colourName
 * @property {HTMLElement} colourDot
 *
 * @extends {Component<StickyRefs>}
 */
class DbStickyComponent extends Component {
  requiredRefs = ['atc'];

  #abort = new AbortController();
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {ResizeObserver | undefined} */
  #sizer;
  /** @type {Set<Element>} */
  #visibleBlockers = new Set();
  #scrolled = false;
  #busy = false;
  #height = 0;

  get #goes() {
    return this.dataset.action === 'go';
  }

  connectedCallback() {
    super.connectedCallback();
    if (!store.ready) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    store.subscribe(() => this.#render(), signal);
    this.refs.atc.addEventListener('click', this.#onClick, { signal });
    this.#render();
    this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#observer?.disconnect();
    this.#sizer?.disconnect();
    document.documentElement.style.removeProperty('--db-bar-h');
  }

  #observe() {
    const { signal } = this.#abort;
    this.#sizer = new ResizeObserver(() => {
      this.#height = this.offsetHeight;
      this.#publishHeight();
    });
    this.#sizer.observe(this);

    if (!('IntersectionObserver' in window)) {
      this.#setVisible(true);
      return;
    }
    const update = () => this.#setVisible(this.#scrolled && this.#visibleBlockers.size === 0);
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#visibleBlockers.add(entry.target);
        else this.#visibleBlockers.delete(entry.target);
      }
      update();
    });
    for (const el of document.querySelectorAll('[data-db-watch="enter"]')) this.#observer.observe(el);
    // The scroll container is `.page-wrapper` on wide screens and the window on phones; getScrollTop() knows which.
    const onScroll = () => {
      const next = getScrollTop() > 40;
      if (next !== this.#scrolled) {
        this.#scrolled = next;
        update();
      }
    };
    document.addEventListener('scroll', onScroll, { passive: true, capture: true, signal });
    onScroll();
  }

  /** @param {boolean} visible */
  #setVisible(visible) {
    this.toggleAttribute('data-visible', visible);
    this.inert = !visible;
    this.#publishHeight();
  }

  /** Anchored scrolling keeps this much clear at the bottom of the screen (see the section stylesheet). */
  #publishHeight() {
    const visible = this.hasAttribute('data-visible');
    document.documentElement.style.setProperty('--db-bar-h', `${visible ? this.#height : 0}px`);
  }

  #onClick = async () => {
    if (this.#goes) {
      emit('db_cta_click', { cta_id: 'bar', colour_key: store.accentVariant?.key ?? '' });
      return;
    }
    if (this.#busy) return;
    this.#busy = true;
    this.refs.atc.classList.add('is-loading');
    const result = await addSelectionToCart(this, 'bar');
    this.refs.atc.classList.remove('is-loading');
    this.#busy = false;
    if (!result.ok) {
      // the main box owns the error text; bring the shopper back to it
      document.querySelector('[data-db-buy-anchor]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  #render() {
    const variant = store.accentVariant;
    const tierData = store.tierData;
    const { atcPrice, colourName, colourDot, atc } = this.refs;
    if (atcPrice && tierData && !this.#goes) atcPrice.textContent = tierData.total;
    if (atc && !this.#busy && !this.#goes) {
      atc.textContent = store.state.tier > 1 && this.dataset.tAddCount ? fill(this.dataset.tAddCount, { __N__: store.state.tier }) : (this.dataset.tAdd ?? atc.textContent);
    }
    if (variant) {
      if (colourName) colourName.textContent = variant.label;
      if (colourDot) colourDot.style.setProperty('--sw', variant.hex);
    }
  }
}

if (!customElements.get('db-sticky-component')) {
  customElements.define('db-sticky-component', DbStickyComponent);
}

/**
 * Add-to-cart button in the closing call to action. Adds the same selection as the buy boxes and
 * shows the squad it will add, so nothing is a surprise in the cart. On a page without a purchase box
 * (data-mode="link") it stays what it says it is: a link to the product page, "Choose your colour".
 *
 * @typedef {Object} CtaRefs
 * @property {HTMLAnchorElement} atc
 * @property {HTMLElement} atcLabel
 * @property {HTMLElement} summary
 * @property {HTMLElement} error
 *
 * @extends {Component<CtaRefs>}
 */
class DbCtaComponent extends Component {
  requiredRefs = ['atc'];

  #abort = new AbortController();
  #busy = false;
  /** @type {number | undefined} */
  #flash;

  get #isLink() {
    return this.dataset.mode === 'link';
  }

  connectedCallback() {
    super.connectedCallback();
    if (!store.ready) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.refs.atc.addEventListener('click', this.#onClick, { signal });
    if (this.#isLink) return;
    // It adds to the cart, so it is announced as a button, and Space works like Enter (a plain link ignores Space).
    this.refs.atc.setAttribute('role', 'button');
    this.refs.atc.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== ' ') return;
        event.preventDefault();
        this.refs.atc.click();
      },
      { signal }
    );
    store.subscribe(() => this.#render(), signal);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    clearTimeout(this.#flash);
  }

  /** @param {Event} event */
  #onClick = async (event) => {
    emit('db_cta_click', { cta_id: 'final', colour_key: store.accentVariant?.key ?? '' });
    // a link to the product page: let the browser follow it
    if (this.#isLink) return;
    // an ordinary link to the product page until this script runs
    event.preventDefault();
    if (this.#busy) return;
    this.#busy = true;
    const { atc, atcLabel, error } = this.refs;
    if (error) error.hidden = true;
    atc.classList.add('is-loading');
    atcLabel.textContent = this.dataset.tAdding ?? '';
    const result = await addSelectionToCart(this, 'final');
    atc.classList.remove('is-loading');
    this.#busy = false;
    if (result.ok) {
      atcLabel.textContent = this.dataset.tAdded ?? '';
      this.#flash = window.setTimeout(() => {
        this.#flash = undefined;
        this.#render();
      }, ADDED_FLASH_MS);
    } else if (error) {
      error.textContent = result.network ? (this.dataset.tErrorNetwork ?? '') : result.message || this.dataset.tError || '';
      error.hidden = false;
      this.#render();
    }
  };

  #render() {
    const { state } = store;
    const { atcLabel, summary } = this.refs;
    if (!this.#busy && !this.#flash && atcLabel) {
      atcLabel.textContent = state.tier > 1 && this.dataset.tAddCount ? fill(this.dataset.tAddCount, { __N__: state.tier }) : (this.dataset.tAdd ?? '');
    }
    if (summary) {
      const names = state.slots.map((id) => store.variant(id)?.label ?? '');
      summary.textContent =
        state.tier === 1 ? '' : fill(this.dataset.tSummary ?? '', { __LIST__: listWords(names, this.dataset.tAnd ?? 'and') });
    }
  }
}

if (!customElements.get('db-cta-component')) {
  customElements.define('db-cta-component', DbCtaComponent);
}

/**
 * Cart drawer upsell: each colour chip adds one more mask in the colour it names, so the shopper can reach the next
 * bundle tier in a colour they chose. The Liquid snippet decides which chips to show (and names them), so the choice
 * survives section re-renders.
 *
 * @typedef {Object} UpsellRefs
 * @property {HTMLElement} error
 *
 * @extends {Component<UpsellRefs>}
 */
class DbUpsellComponent extends Component {
  #busy = false;

  /** @param {Event} event */
  async handleAdd(event) {
    const chip = /** @type {HTMLElement} */ (event.target).closest('[data-variant-id]');
    if (this.#busy || !(chip instanceof HTMLElement)) return;
    const id = Number(chip.dataset.variantId);
    if (!id) return;
    this.#busy = true;
    const { error } = this.refs;
    chip.classList.add('is-loading');
    if (error) error.hidden = true;
    const result = await addLinesToCart(this, [{ id, quantity: 1 }], { location: 'cart' });
    chip.classList.remove('is-loading');
    this.#busy = false;
    if (!result.ok && error) {
      error.textContent = this.dataset.tError ?? '';
      error.hidden = false;
    }
  }
}

if (!customElements.get('db-upsell-component')) {
  customElements.define('db-upsell-component', DbUpsellComponent);
}
